import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import ChatInterface from './pages/ChatInterface';
import { AuthHandler } from './components/AuthHandler';
import AuthError from './components/AuthError';

import LandingPage from './components/LandingPage';
import AdminLandingPage from './pages/AdminLandingPage';
import AdminPanel from './pages/AdminPanel';
import AgentFactory from './pages/AgentFactory';
import BuildAnAgent from './pages/BuildAnAgent';
import MyBuiltAgents from './pages/MyBuiltAgents';
import DevAdmin from './pages/DevAdmin';
import AldoDashboard from './pages/AldoDashboard';
import HRPolicyDashboard from './pages/HRPolicyDashboard';
import PWMSDashboard from './pages/PWMSDashboard';
import ProductDashboard from './pages/ProductDashboard';
import UniFirstDocsDashboard from './pages/UniFirstDocsDashboard';
import OrientationDashboard from './pages/OrientationDashboard';
import IRISDashboard from './pages/IRISDashboard';
import ServiceDeskDashboard from './pages/ServiceDeskDashboard';
import OwensboroDashboard from './pages/OwensboroDashboard';
import DirectEmbroideryDashboard from './pages/DirectEmbroideryDashboard';
import { IndexingDashboard } from './pages/admin/IndexingDashboard';
import PatchNotes from './pages/PatchNotes';
import GraphAPITest from './pages/GraphAPITest';

import { Agent, Message, User, SendMessageOptions } from './types';
import { useAgents } from './hooks/useAgents';
import { useAgentInteractions } from './hooks/useAgentInteractions';
import { useSession } from './hooks/useSession';
import { useChat } from './hooks/useChat';
import { useChatHistory } from './hooks/useChatHistory';
import { useAppearance } from './hooks/useAppearance';
import { useAuth } from './hooks/useAuth';
import { useStreamingPreference } from './hooks/useStreamingPreference';
import { useUserProfile } from './hooks/useUserProfile';
import { useLayout } from './hooks/useLayout';
import { cancelAllImagePreloads } from './hooks/useImagePreloader';
import { checkVersionAndClearCache } from './utils/cacheBusting';
import { formatWhatsNewBlock } from './utils/whatsNew';
import { checkForUrlAuthErrors } from './utils/authErrorDetection';
import KeyVaultConfigService from './services/keyVaultConfig';
import { applicationInsights } from './services/applicationInsights';
import { useMonitoring } from './services/monitoring';
import { useApplicationInsights } from './services/applicationInsights';
import DevTestingPanel from './components/DevTestingPanel';

import './App.css';
import AuthDebug from './components/AuthDebug';

// Session storage keys for persisting chat state across refreshes
const SESSION_KEYS = {
  SELECTED_AGENT_ID: 'askaldo_selected_agent_id',
  IS_AGENT_VIA_CARD: 'askaldo_agent_via_card',
  LAST_ROUTED_AGENT_ID: 'askaldo_last_routed_agent_id',
  ACTIVE_ROUTE: 'askaldo_active_route',
} as const;

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocationRef = useRef<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [lastRoutedAgentId, setLastRoutedAgentId] = useState<string | null>(null);
  // Keep agents in a ref so the callback always has access to the latest
  const agentsRef = useRef<Agent[]>([]);
  // Track if we're already waiting for agents to prevent multiple retry chains
  const waitingForAgentsRef = useRef<boolean>(false);
  // Track whether we've already attempted to restore the session
  const hasAttemptedRestoreRef = useRef<boolean>(false);
  // Analytics and monitoring hooks
  const { trackUserEvent} = useMonitoring();
  const { trackEvent } = useApplicationInsights();
  const [messages, setMessages] = useState<Message[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isAgentSelectedViaCard, setIsAgentSelectedViaCard] = useState<boolean>(false);
  const [showDebug, setShowDebug] = useState<boolean>(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);


  
  // Authentication hook - MUST be called first
  const { 
    user: authUser, 
    isAuthenticated, 
    isLoading: authLoading,
    getUserDisplayName,
    getUserEmail,
    getUserAvatar,
    getUserRoles,
    logout
  } = useAuth();

  // Temporary release gate: the self-service agent factory (/build-agent,
  // /my-agents) is limited to super admins. Roles mirror the backend
  // getEntraSuperAdminRoles() enforced on /api/me/agents.
  const isSelfServiceAdmin = (getUserRoles ? getUserRoles() : []).some(
    (r: string) => ['super-admin', 'askaldo-admin', 'admin'].includes(r)
  );
  
  // User profile hook - fetch Graph API data for personalization
  const { profile: graphUserProfile } = useUserProfile(true);
  
  // Backend integration hooks - MUST be called before any conditional returns
  const { agents, userProfile: backendUserProfile, reloadAgents } = useAgents();
  const { hasSeenAgent, getLastSeenVersion } = useAgentInteractions();
  const { sessionId, userId: sessionUserId, createNewSession } = useSession();

  const defaultAldoAgent = useMemo(() => {
    if (!agents || agents.length === 0) {
      return null;
    }
    return (
      agents.find(a => a.id === 'aldo') ||
      agents.find(a => a.id === 'aldo-unified-agent') ||
      agents.find(a => a.id.toLowerCase().includes('aldo')) ||
      null
    );
  }, [agents]);

  const accessibleAgentIds = useMemo<string[]>(() => {
    if (backendUserProfile?.accessibleAgents && backendUserProfile.accessibleAgents.length > 0) {
      return backendUserProfile.accessibleAgents;
    }
    return agents.map(agent => agent.id);
  }, [backendUserProfile, agents]);

  // Filter out hidden agents for routing - Aldo should only route to visible agents
  const routingAccessibleAgentIds = useMemo<string[]>(() => {
    const visibleAgents = agents.filter(agent => !agent.hidden);
    const normalized = new Set(accessibleAgentIds.map(id => id.toLowerCase()));
    return visibleAgents
      .filter(agent => normalized.has(agent.id.toLowerCase()))
      .map(agent => agent.id);
  }, [agents, accessibleAgentIds]);

  const mentionableAgents = useMemo(() => {
    if (!agents || agents.length === 0) {
      return [];
    }
    if (!accessibleAgentIds || accessibleAgentIds.length === 0) {
      return agents.filter(agent => !agent.hidden);
    }
    const normalized = new Set(accessibleAgentIds.map(id => id.toLowerCase()));
    return agents.filter(agent => 
      !agent.hidden && normalized.has(agent.id.toLowerCase())
    );
  }, [agents, accessibleAgentIds]);
  
  // Keep agents ref in sync with agents state
  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);
  
  // Use authenticated user ID when available, fall back to session ID
  const effectiveUserId = useMemo(() => {
    if (isAuthenticated && authUser?.userId) {
      console.log('🔑 Using authenticated user ID:', authUser.userId);
      return authUser.userId;
    }
    console.log('⚠️ No authenticated user ID, using session fallback:', sessionUserId);
    return sessionUserId;
  }, [isAuthenticated, authUser, sessionUserId]);
  
  const { 
    sendMessage, 
    regenerateMessage, 
    isAgentTyping, 
    isStreaming, 
    currentStreamingMessage, 
    toolCalls,
    currentToolCall,
    error: chatError, 
    productDisplays,
    alternativeDisplays,  // For [ALTERNATIVES: anchor_id] marker replacement
    clearError,
    cancelActiveStream,
    handleAlternativeSelect,
    clearProductCache  // Clear product displays/alternatives on conversation switch
  } = useChat(sessionId, effectiveUserId, setStatusMessage, useCallback((newAgentName: string, toAgentId?: string) => {
    const currentAgents = agentsRef.current;
    
    console.log('🔄 AGENT SWITCH CALLBACK TRIGGERED:', {
      newAgentName,
      toAgentId,
      currentLastRoutedAgentId: lastRoutedAgentId
    });
    
    if (!newAgentName && !toAgentId) {
      console.warn('⚠️ Agent switch called with no name or ID!');
      return;
    }
    
    const routedAgent = (toAgentId ? currentAgents.find(a => a.id === toAgentId) : undefined) || 
                       currentAgents.find(a => a.name === newAgentName);
    
    if (routedAgent) {
      console.log(`✅ Setting lastRoutedAgentId to: ${routedAgent.id}`);
      setLastRoutedAgentId(routedAgent.id);
    } else if (toAgentId) {
      console.log(`✅ Setting lastRoutedAgentId to toAgentId: ${toAgentId}`);
      setLastRoutedAgentId(toAgentId);
    } else {
      console.warn('⚠️  Agent switch callback could not find agent to set!');
    }
  }, [lastRoutedAgentId, setLastRoutedAgentId]), agents);
  
  // Clean up active streams when route changes (user navigates away from chat)
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;
    
    // If navigating away from a chat (route changed)
    if (previousPath && previousPath !== currentPath) {
      console.log('🧹 Route changed - canceling any active streams', {
        from: previousPath,
        to: currentPath
      });
      cancelActiveStream();
    }
    
    // Update the ref for next comparison
    previousLocationRef.current = currentPath;
    
    // Cleanup on unmount - cancel all streams
    return () => {
      console.log('🧹 Component unmounting - canceling any active streams');
      cancelActiveStream();
    };
  }, [location.pathname, cancelActiveStream]);
  
  // Debug productDisplays state
  // useEffect(() => {
  //   console.log('🔍 APP.TSX PRODUCT DISPLAYS STATE:', {
  //     hasProductDisplays: !!productDisplays,
  //     keys: productDisplays ? Object.keys(productDisplays) : [],
  //     isEmpty: productDisplays && Object.keys(productDisplays).length === 0,
  //     content: productDisplays
  //   });
  // }, [productDisplays]);
  
  // Streaming preference hook
  const { isStreamingEnabled, toggleStreaming } = useStreamingPreference();
  
  // Conversation history hook
  const {
    conversations,
    currentConversationId,
    saveConversation,
    loadConversation,
    loadConversationFull,
    deleteConversation,
    clearAllConversations,
    startNewConversation,
    refreshConversations,
    setActiveConversation,
    isLoading: chatHistoryLoading
  } = useChatHistory();

  // Global callback detector - runs before anything else
  useEffect(() => {
    const currentUrl = window.location.href;
    const pathname = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    
    console.log('🌍 Global App.tsx URL check:', {
      url: currentUrl,
      pathname,
      search,
      hash
    });
    
    // Check if this looks like an OIDC callback
    const hasCode = search.includes('code=') || hash.includes('code=');
    const hasState = search.includes('state=') || hash.includes('state=');
    const isAuthPath = pathname.includes('/auth/');
    
    if ((hasCode || hasState) && !isAuthPath) {
      console.log('🔄 Detected callback params without auth path - redirecting to /auth/callback');
      // Redirect to auth path while preserving all parameters
      const newUrl = `/auth/callback${search}${hash}`;
      window.history.replaceState(null, '', newUrl);
      window.location.reload();
      return;
    }
    
    if (hasCode || hasState || pathname.includes('callback')) {
      console.log('🎯 OIDC Callback detected in App.tsx!', {
        hasCode,
        hasState,
        isAuthPath,
        pathname
      });
    }
  }, []);

  // Initialize appearance settings
  useAppearance();
  
  // Layout management hook
  const {
    isMobile,
    isTablet,
    // isDesktop, // Reserved for future use
    sidebarOpen,
    toggleSidebar,
  } = useLayout();

  // --- Persist active chat state to sessionStorage so it survives page refreshes ---
  useEffect(() => {
    if (selectedAgent) {
      sessionStorage.setItem(SESSION_KEYS.SELECTED_AGENT_ID, selectedAgent.id);
    } else {
      sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
    }
  }, [selectedAgent]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEYS.IS_AGENT_VIA_CARD, String(isAgentSelectedViaCard));
  }, [isAgentSelectedViaCard]);

  useEffect(() => {
    if (lastRoutedAgentId) {
      sessionStorage.setItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID, lastRoutedAgentId);
    } else {
      sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);
    }
  }, [lastRoutedAgentId]);

  // Persist current route so we can restore it on refresh
  useEffect(() => {
    // Only persist non-auth routes
    if (!location.pathname.startsWith('/auth')) {
      sessionStorage.setItem(SESSION_KEYS.ACTIVE_ROUTE, location.pathname);
    }
  }, [location.pathname]);

  // --- Auto-restore chat session after page refresh ---
  // This effect runs once when agents load and chat history is ready, restoring the previous conversation.
  // It depends on currentConversationId being restored from localStorage by useChatHistory
  // (which signals the history service is initialized).
  useEffect(() => {
    // Only attempt restore once, and only when agents are available
    if (hasAttemptedRestoreRef.current || !agents || agents.length === 0) return;
    // Don't restore while chat history is still loading
    if (chatHistoryLoading) return;
    // Don't restore if user already has an active agent (e.g., navigated via link)
    if (selectedAgent) {
      hasAttemptedRestoreRef.current = true;
      return;
    }
    // Don't restore if not on the root route (admin pages handle themselves)
    if (location.pathname !== '/') {
      hasAttemptedRestoreRef.current = true;
      return;
    }

    const savedAgentId = sessionStorage.getItem(SESSION_KEYS.SELECTED_AGENT_ID);
    const savedConversationId = currentConversationId || localStorage.getItem('unichat_current_conversation');
    const savedViaCard = sessionStorage.getItem(SESSION_KEYS.IS_AGENT_VIA_CARD) === 'true';
    const savedLastRoutedId = sessionStorage.getItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);

    if (savedAgentId && savedConversationId) {
      const agent = agents.find(a => a.id === savedAgentId);
      if (agent) {
        console.log('🔄 Restoring chat session after refresh:', {
          agentId: savedAgentId,
          conversationId: savedConversationId,
          viaCard: savedViaCard
        });
        setSelectedAgent(agent);
        setIsAgentSelectedViaCard(savedViaCard);
        if (savedLastRoutedId) {
          setLastRoutedAgentId(savedLastRoutedId);
        }
        // Auto-load the conversation from history
        const restoreConversation = async () => {
          try {
            const conversation = await loadConversationFull(savedConversationId);
            if (conversation) {
              // Mark as loaded from history to prevent unnecessary re-saves
              isLoadedFromHistoryRef.current = true;
              loadedConversationMessageCountRef.current = conversation.messages.length;
              const lastLoadedMessage = conversation.messages[conversation.messages.length - 1];
              if (lastLoadedMessage) {
                lastSavedMessageIdRef.current = lastLoadedMessage.id;
              }
              setActiveConversation(savedConversationId);
              setMessages(conversation.messages);
              console.log('✅ Chat session restored:', conversation.messages.length, 'messages');
            }
          } catch (error) {
            console.warn('⚠️ Failed to restore conversation on refresh:', error);
          }
        };
        restoreConversation();
      }
    }

    hasAttemptedRestoreRef.current = true;
  }, [agents, selectedAgent, location.pathname, loadConversationFull, setActiveConversation, chatHistoryLoading, currentConversationId]);

  // --- Restore non-root route after page refresh ---
  // If the user was on an admin page, navigate back to it
  useEffect(() => {
    const savedRoute = sessionStorage.getItem(SESSION_KEYS.ACTIVE_ROUTE);
    if (
      savedRoute &&
      savedRoute !== '/' &&
      !savedRoute.startsWith('/auth') &&
      location.pathname === '/' &&
      isAuthenticated &&
      !authLoading
    ) {
      console.log('🔄 Restoring route after refresh:', savedRoute);
      navigate(savedRoute, { replace: true });
    }
  // Only run on initial mount after auth resolves
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  // Enhanced cache busting and auth error checking on app startup
  useEffect(() => {
    // Check for URL-based auth errors first
    if (checkForUrlAuthErrors()) {
      console.log('🚫 URL auth error detected on app startup, redirecting...');
      return; // Exit early, redirect will be handled by checkForUrlAuthErrors
    }
    
    checkVersionAndClearCache();
  }, []);

  // Initialize Application Insights
  useEffect(() => {
    applicationInsights.initialize();
    console.log('🔧 Application Insights initialization completed');
  }, []);

  // Set user context for Application Insights when authentication changes
  useEffect(() => {
    if (isAuthenticated && authUser) {
      const userEmail = getUserEmail();
      const userId = userEmail || authUser.userId || 'anonymous';
      applicationInsights.setUserContext(userId, 'unifirst');
      
      // Track user login
      applicationInsights.trackEvent('UserAuthenticated', {
        userId: userId,
        userEmail: userEmail || 'unknown',
        authProvider: 'oidc'
      });
    }
  }, [isAuthenticated, authUser, getUserEmail]);

  // Prevent unwanted scroll restoration on focus (fixes tab-back scroll issues)
  useEffect(() => {
    // Disable automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Track window focus to prevent scroll jumps
    let preventScrollTimeout: NodeJS.Timeout;
    
    const handleWindowFocus = () => {
      // Prevent scrolling for a short period after focus
      preventScrollTimeout = setTimeout(() => {
        // Focus handling complete
      }, 300);
    };

    const handleWindowBlur = () => {
      if (preventScrollTimeout) {
        clearTimeout(preventScrollTimeout);
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
      if (preventScrollTimeout) {
        clearTimeout(preventScrollTimeout);
      }
    };
  }, []);

  // Debug mode keyboard shortcut (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setShowDebug(prev => !prev);
        // Debug mode toggle - no logging needed
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDebug]);

  // Create user object for UI components (memoized to prevent continuous re-creation)
  const user: User = useMemo(() => ({
    id: getUserEmail() || authUser?.userId || 'anonymous',
    name: getUserDisplayName(),
    avatar: getUserAvatar(),
    status: isAuthenticated ? 'online' : 'offline'
  }), [getUserEmail, getUserDisplayName, getUserAvatar, authUser?.userId, isAuthenticated]);

  // Auto-save conversation when messages change (with improved race condition handling)
  const lastSavedMessageIdRef = useRef<string | null>(null);
  const lastSaveAtRef = useRef<number>(0);
  // Track when conversation is loaded from history to prevent re-saving without interaction
  const isLoadedFromHistoryRef = useRef<boolean>(false);
  const loadedConversationMessageCountRef = useRef<number>(0);

  useEffect(() => {
    if (!selectedAgent || messages.length < 2) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender !== 'agent' || !lastMessage.isCompleted) {
      return;
    }

    if (lastSavedMessageIdRef.current === lastMessage.id) {
      return;
    }

    // If this conversation was loaded from history and no new messages were added, skip save
    // This prevents updating timestamp/position when just opening a conversation
    if (isLoadedFromHistoryRef.current && messages.length <= loadedConversationMessageCountRef.current) {
      console.log('📖 Conversation loaded from history - skipping auto-save (no new interaction)');
      // Still update the ref to prevent future unnecessary save attempts
      lastSavedMessageIdRef.current = lastMessage.id;
      return;
    }

    const meaningfulMessages = messages.filter(msg =>
      msg.content.trim().length > 0 &&
      msg.sender !== 'system' &&
      !msg.isStatusMessage &&
      (msg.isCompleted === undefined || msg.isCompleted === true)
    );

    if (meaningfulMessages.length === 0) {
      return;
    }

    const now = Date.now();
    if (now - lastSaveAtRef.current < 500) {
      return;
    }

    const saveTimeout = setTimeout(async () => {
      try {
        await saveConversation(meaningfulMessages, selectedAgent);
        lastSavedMessageIdRef.current = lastMessage.id;
        lastSaveAtRef.current = Date.now();
        // Clear the loaded-from-history flag after first real save (new interaction occurred)
        if (isLoadedFromHistoryRef.current) {
          isLoadedFromHistoryRef.current = false;
        }
      } catch (error) {
        console.warn('Failed to save conversation:', error);
      }
    }, 300);

    return () => {
      clearTimeout(saveTimeout);
    };
  }, [messages, selectedAgent, saveConversation]);

  // Build a snapshot of messages, finalizing the in-flight agent message if streaming
  const computeSnapshotMessages = useCallback((): Message[] => {
    if (!messages || messages.length === 0) return [];
    if (!isStreaming || !currentStreamingMessage) {
      return messages;
    }
    // Find the latest incomplete streamed agent message
    let index = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i] as any;
      if (m && m.sender === 'agent' && m.wasStreamed && m.isCompleted === false) {
        index = i;
        break;
      }
    }
    if (index === -1) return messages;
    const finalized = { 
      ...messages[index], 
      content: currentStreamingMessage, 
      isCompleted: true, 
      wasStreamed: true 
    } as Message;
    return [
      ...messages.slice(0, index),
      finalized,
      ...messages.slice(index + 1)
    ];
  }, [messages, isStreaming, currentStreamingMessage]);

  // Save a snapshot of the current conversation (including finalized partial agent reply)
  // Only saves if there were new interactions (not just viewing a loaded conversation)
  const saveSnapshotIfNeeded = useCallback(async (): Promise<string | null> => {
    try {
      if (!selectedAgent) return null;
      const snapshot = computeSnapshotMessages();
      if (!snapshot || snapshot.length === 0) return null;
      
      // Skip save if conversation was loaded from history and no new messages were added
      // This prevents updating the timestamp/position when just viewing a conversation
      if (isLoadedFromHistoryRef.current && snapshot.length <= loadedConversationMessageCountRef.current) {
        console.log('📖 Skipping snapshot save - conversation loaded from history with no new interaction');
        return null;
      }
      
      return await saveConversation(snapshot, selectedAgent);
    } catch (e) {
      console.warn('📄 Snapshot save failed:', e);
      return null;
    }
  }, [selectedAgent, computeSnapshotMessages, saveConversation]);

  const handleNewChat = useCallback(async () => {
    // New chat button clicked
    // Save snapshot first, then cancel stream
    await saveSnapshotIfNeeded();
    cancelActiveStream();
    
    // Clear product image cache and product displays on conversation switch
    cancelAllImagePreloads();
    clearProductCache();

    // Reset history-loaded flags for fresh conversation
    isLoadedFromHistoryRef.current = false;
    loadedConversationMessageCountRef.current = 0;
    lastSavedMessageIdRef.current = null;

    // Clear persisted session state so refresh shows landing page
    sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
    sessionStorage.removeItem(SESSION_KEYS.IS_AGENT_VIA_CARD);
    sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);

    startNewConversation(); // Start new conversation first
    setMessages([]);
    setSelectedAgent(null);
    setLastRoutedAgentId(null); // Clear routing context for new conversation
    setIsAgentSelectedViaCard(false); // Reset the selection method flag
    // Don't create new session here - it will be created when Aldo session starts
    navigate('/');
  }, [navigate, startNewConversation, saveSnapshotIfNeeded, cancelActiveStream, setLastRoutedAgentId, clearProductCache]); // currentConversationId is not used in the callback

  const handleSendMessage = useCallback(async (content: string, options?: SendMessageOptions) => {
    // CRITICAL: If forcedAgentId is specified (via /@ agent selection), route directly to that agent
    // Otherwise, ALWAYS route to Aldo and let Aldo's LLM routing decide (with followup awareness)
    // EXCEPTION: If isAgentSelectedViaCard is true ("locked chat"), ALWAYS route directly to selectedAgent
    let agentToUse: Agent | null = null;
    
    // LOCKED CHAT MODE: When agent was accessed via agent card modal, 
    // bypass Aldo routing and send directly to the selected agent
    if (isAgentSelectedViaCard && selectedAgent && !selectedAgent.hidden) {
      agentToUse = selectedAgent;
      // Ensure forcedAgentId is set so the API uses the direct agent endpoint
      if (!options) {
        options = { forcedAgentId: selectedAgent.id };
      } else {
        options.forcedAgentId = selectedAgent.id;
      }
      console.log('🔒 LOCKED CHAT: Routing directly to', agentToUse.name, '(bypassing Aldo)');
    } else if (options?.forcedAgentId) {
      // Find the forced agent from available agents
      const forcedId = options.forcedAgentId;
      agentToUse = agents.find(a => a.id === forcedId) || null;
      
      if (!agentToUse) {
        console.warn('❌ Forced agent not found or not accessible:', options.forcedAgentId);
        // Fall back to Aldo for routing
        agentToUse = defaultAldoAgent;
        // Clear the forced routing since we're falling back - let Aldo handle it
        options.forcedAgentId = null;
      } else if (agentToUse.hidden) {
        // CRITICAL: Cannot force-route to hidden agents (like Aldo itself)
        // Hidden agents are for routing only, not direct messaging
        console.warn('❌ Cannot force-route to hidden agent:', agentToUse.id, '- routing through Aldo instead');
        agentToUse = defaultAldoAgent;
        // Clear the forced routing since we're falling back
        options.forcedAgentId = null;
      } else {
        console.log('✅ FORCED ROUTING: Bypassing Aldo, routing directly to', agentToUse.name);
        // Update the selected agent to the forced agent for UI consistency
        if (selectedAgent?.id !== agentToUse.id) {
          setSelectedAgent(agentToUse);
        }
      }
    } else {
      // Default: route through Aldo and let its LLM routing decide
      agentToUse = defaultAldoAgent;
    }

    if (!agentToUse) {
      console.error('❌ No Aldo agent available to send message');
      return;
    }

    if (!selectedAgent) {
      setSelectedAgent(agentToUse);
    }

    const messageContext: any = {
      routing: {
        accessibleAgents: routingAccessibleAgentIds,
        lastRoutedAgentId, // surfaced as a neutral prior-turn note only; no routing boost
        forcedAgentId: options?.forcedAgentId || null,
        conversationId: currentConversationId,
        messageCount: messages.length
      }
    };
    
    console.log('📦 MESSAGE CONTEXT ROUTING:', {
      lastRoutedAgentId,
      accessibleAgents: routingAccessibleAgentIds,
      forcedAgentId: options?.forcedAgentId || null
    });

    if (options?.productContext) {
      messageContext.pinnedProducts = options.productContext.pinnedProducts;
      messageContext.activeProducts = options.productContext.activeProducts;
    }

    if (options?.userContext) {
      messageContext.userContext = options.userContext;
    }

    // Include fast mode preference (defaults to false = Precise mode)
    messageContext.fastMode = options?.fastMode ?? false;

    console.log('🚀 SENDING MESSAGE:', {
      selectedAgentId: agentToUse.id,
      forcedAgentId: options?.forcedAgentId || null,
      lastRoutedAgentId,
      messagePreview: content.substring(0, 50),
      isFollowUp: messages.length > 0
    });

    try {
      await sendMessage(
        content,
        agentToUse,
        messages,
        setMessages,
        isStreamingEnabled,
        0,
        messageContext
      );
      
      // CRITICAL: Only set lastRoutedAgentId for FORCED routing (direct agent selection)
      // For Aldo routing, the onAgentSwitch callback will set it when the actual routed agent is determined
      if (options?.forcedAgentId) {
        console.log(`📌 Setting lastRoutedAgentId to forced agent: ${agentToUse.id}`);
        setLastRoutedAgentId(agentToUse.id);
      } else {
        console.log(`🔄 Letting onAgentSwitch set lastRoutedAgentId for Aldo routing`);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [selectedAgent, defaultAldoAgent, accessibleAgentIds, lastRoutedAgentId, currentConversationId, messages, isStreamingEnabled, sendMessage, setSelectedAgent, isAgentSelectedViaCard, agents]);

  const handleBackToAgents = useCallback(async () => {
    // Cancel any active streams before navigating away
    cancelActiveStream();
    
    // Save current conversation snapshot if needed before clearing
    await saveSnapshotIfNeeded();
    
    // Reset history-loaded flags for fresh conversation
    isLoadedFromHistoryRef.current = false;
    loadedConversationMessageCountRef.current = 0;
    lastSavedMessageIdRef.current = null;
    
    // Clear persisted session state so refresh shows landing page
    sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
    sessionStorage.removeItem(SESSION_KEYS.IS_AGENT_VIA_CARD);
    sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);
    
    // Clear all state for clean slate
    setSelectedAgent(null);
    setLastRoutedAgentId(null);
    setMessages([]);
    
    // Start fresh conversation context
    startNewConversation();
    
    navigate('/');
  }, [navigate, cancelActiveStream, saveSnapshotIfNeeded, startNewConversation]);

  const handleLogoClick = useCallback(async () => {
    // Cancel any active streams before navigating away
    cancelActiveStream();
    
    // Save current conversation snapshot if needed before clearing
    await saveSnapshotIfNeeded();
    
    // Reset history-loaded flags for fresh conversation
    isLoadedFromHistoryRef.current = false;
    loadedConversationMessageCountRef.current = 0;
    lastSavedMessageIdRef.current = null;
    
    // Clear persisted session state so refresh shows landing page
    sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
    sessionStorage.removeItem(SESSION_KEYS.IS_AGENT_VIA_CARD);
    sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);
    
    // Same functionality as back to agents - return to landing page
    setSelectedAgent(null);
    setLastRoutedAgentId(null);
    setMessages([]);
    setIsAgentSelectedViaCard(false);
    
    // Start fresh conversation context
    startNewConversation();
    
    navigate('/');
  }, [navigate, cancelActiveStream, saveSnapshotIfNeeded, startNewConversation]);



  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Handle question submission from landing page - auto-select Aldo and start conversation
  // If targetAgentId is provided (from agent card), we route directly to that agent (locked chat)
  const handleLandingPageQuestion = useCallback(async (question: string, targetAgentId?: string, retryCount: number = 0) => {
    const MAX_RETRIES = 10; // Maximum 5 seconds of retrying
    
    // Wait for agents to load if they haven't yet
    if (!agents || agents.length === 0) {
      if (retryCount >= MAX_RETRIES) {
        console.error('Failed to load agents after maximum retries');
        return;
      }
      console.warn(`Agents not loaded yet, waiting... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      setTimeout(() => handleLandingPageQuestion(question, targetAgentId, retryCount + 1), 500);
      return;
    }

    // If targetAgentId is specified (from agent card click), this is a "locked chat" to that specific agent
    // The UI should show that agent's name, not Aldo
    let selectedAgentForUI: Agent | null = null;
    let isDirectAgentAccess = false;
    
    if (targetAgentId) {
      const targetAgent = agents.find((a: Agent) => a.id === targetAgentId);
      if (targetAgent && !targetAgent.hidden) {
        // Direct agent access - show this agent in the UI header
        selectedAgentForUI = targetAgent;
        isDirectAgentAccess = true;
        console.log('🎯 Direct agent access via card:', targetAgent.name);
      }
    }
    
    // If not direct agent access, use Aldo for routing
    if (!selectedAgentForUI) {
      console.log('🔍 [ALDO DEBUG] defaultAldoAgent:', defaultAldoAgent?.id, '| agents count:', agents.length, '| agent ids:', agents.map((a: Agent) => a.id).join(','));
      selectedAgentForUI = defaultAldoAgent || agents.find((a: Agent) => a.id.toLowerCase().includes('aldo')) || agents[0];
    }
    
    if (!selectedAgentForUI) {
      console.error('No agent found – unable to send question', {
        agentsAvailable: agents.map(a => a.id),
        defaultAldoAgent,
        agentsLength: agents.length
      });
      return;
    }

    console.log('✅ Selected agent for UI:', selectedAgentForUI.id, isDirectAgentAccess ? '(direct access)' : '(via Aldo)');
    setSelectedAgent(selectedAgentForUI);
    setMessages([]);
    setIsAgentSelectedViaCard(isDirectAgentAccess);
    setLastRoutedAgentId(isDirectAgentAccess && targetAgentId ? targetAgentId : null);
    createNewSession();

    // Only send message if there's actual content
    // Empty question with targetAgentId means "Start Conversation" - just show welcome message
    if (question.trim()) {
      setTimeout(() => {
        handleSendMessage(question, { forcedAgentId: targetAgentId });
      }, 100);
    } else if (isDirectAgentAccess) {
      // For empty question with direct agent access, mock stream the welcome message.
      //
      // If the user has already interacted with the CURRENT version of this agent
      // (i.e. it would not be flagged as NEW or UPDATED on the home screen) AND the
      // agent provides a `shortWelcomeMessage`, stream the short version and expose
      // the full version behind an expand toggle in the chat UI.
      const fullWelcome = selectedAgentForUI.welcomeMessage || `Hi! I'm your ${selectedAgentForUI.name} agent. How can I help you today?`;
      const shortWelcome = selectedAgentForUI.shortWelcomeMessage;
      const currentVersion = selectedAgentForUI.version;
      const isReturningCurrentUser = !!shortWelcome
        && hasSeenAgent(selectedAgentForUI.id)
        && !!currentVersion
        && getLastSeenVersion(selectedAgentForUI.id) === currentVersion;

      // "What's New" injection: prepend a dated What's New banner to the intro
      // whenever the agent has a non-empty update-notice summary. This is
      // GLOBAL and persistent for every user in the env (sourced from
      // agents.json) — it shows on every intro render until an admin clears the
      // summary. It is driven purely by the summary text, not the `enabled`
      // flag (which only governs the home-screen UPDATED badge / float) and not
      // per-browser interaction history (which would hide it inconsistently).
      const notice = selectedAgentForUI.updateNotice;
      const noticeActive = !!notice && !!(notice.summary && notice.summary.trim());

      const whatsNewBlock = noticeActive ? formatWhatsNewBlock(notice) : '';

      // A What's-New banner is only meaningful on the full intro, so when it is
      // active we always show the full welcome (never the condensed returning form).
      const baseContent = (isReturningCurrentUser && !noticeActive) ? (shortWelcome as string) : fullWelcome;
      const fullContent = whatsNewBlock + baseContent;
      const metadata: Record<string, any> | undefined = (isReturningCurrentUser && !noticeActive)
        ? { fullWelcomeMessage: fullWelcome, shortFormVersion: currentVersion }
        : undefined;

      // Start with empty content, then stream in
      const welcomeMessage: Message = {
        id: 'welcome-message',
        content: '',
        sender: 'agent',
        timestamp: new Date(),
        agentName: selectedAgentForUI.name,
        isCompleted: false,
        ...(metadata && { metadata })
      };
      setMessages([welcomeMessage]);
      
      // Mock stream the content character by character (fast)
      let currentIndex = 0;
      const streamInterval = setInterval(() => {
        // Stream 3-5 characters at a time for faster but still visible streaming effect
        const charsToAdd = Math.min(4, fullContent.length - currentIndex);
        currentIndex += charsToAdd;
        
        const streamedContent = fullContent.substring(0, currentIndex);
        const isComplete = currentIndex >= fullContent.length;
        
        setMessages([{
          ...welcomeMessage,
          content: streamedContent,
          isCompleted: isComplete
        }]);
        
        if (isComplete) {
          clearInterval(streamInterval);
        }
      }, 8); // 8ms interval for fast streaming
    }
  }, [agents, defaultAldoAgent, createNewSession, handleSendMessage, setLastRoutedAgentId, hasSeenAgent, getLastSeenVersion]);

  // -------------------------------------------------------------------------
  // ?agent=<id> deep-link bridge
  //
  // The "Chat" button on the My Built Agents page navigates to
  // `/?agent=<id>` to open a fresh conversation pinned to a specific
  // self-service agent. Without this effect nothing reads that param,
  // so the user lands on an unpinned chat and the next message routes
  // through Aldo's hand-off — which (a) skips the agent's welcome and
  // (b) lets Aldo delegate to other specialists. Both bugs were
  // observable on the same path; fixing the bridge fixes both.
  //
  // The bridge mirrors the landing-page "Start Conversation" flow:
  // it calls `handleLandingPageQuestion('', agentId)` which sets the
  // locked-chat flag, kicks off the welcome stream, and ensures
  // subsequent sends go directly to /{agentId}/chat/stream rather
  // than to Aldo. We strip the query param off the URL after firing
  // so a refresh doesn't keep re-triggering the welcome (and so
  // saving / sharing the URL doesn't auto-restart the conversation
  // every time you visit it).
  //
  // The hasFiredAgentParamRef guards against the agents array
  // becoming truthy a second time (e.g. background refresh) and
  // re-firing the bridge mid-conversation.
  // -------------------------------------------------------------------------
  const hasFiredAgentParamRef = useRef(false);
  useEffect(() => {
    if (hasFiredAgentParamRef.current) return;
    if (!isAuthenticated) return;
    if (!agents || agents.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const requestedAgentId = params.get('agent');
    if (!requestedAgentId) return;

    hasFiredAgentParamRef.current = true;
    console.log('🔗 ?agent= deep link → opening locked chat for', requestedAgentId);

    // Strip the param from the URL before firing the welcome flow.
    // We keep the rest of the search (and the path/hash) intact in
    // case other features need them — only the `agent` key is
    // removed.
    params.delete('agent');
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname +
      (newSearch ? `?${newSearch}` : '') +
      window.location.hash;
    window.history.replaceState(null, '', newUrl);

    handleLandingPageQuestion('', requestedAgentId);
  }, [agents, isAuthenticated, handleLandingPageQuestion]);

  // Conversation history handlers
  const handleLoadConversation = useCallback(async (conversationId: string) => {
    try {
      console.log('📖 Loading conversation:', conversationId);
      // Save snapshot of current conversation, then cancel stream before switching
      await saveSnapshotIfNeeded();
      cancelActiveStream();
      
      // Clear product image cache and product displays on conversation switch
      cancelAllImagePreloads();
      clearProductCache();
      
      const conversation = await loadConversationFull(conversationId);
      if (conversation) {
        // Mark that this conversation was loaded from history (not a new interaction)
        // This prevents auto-save from updating the timestamp/position when just opening
        isLoadedFromHistoryRef.current = true;
        loadedConversationMessageCountRef.current = conversation.messages.length;
        
        // Set the last saved message ID to the last message in the loaded conversation
        // to prevent unnecessary re-saves
        const lastLoadedMessage = conversation.messages[conversation.messages.length - 1];
        if (lastLoadedMessage) {
          lastSavedMessageIdRef.current = lastLoadedMessage.id;
        }
        
        // Set this conversation as the active one
        setActiveConversation(conversationId);
        setMessages(conversation.messages);
        // Use stored agent metadata to lock routing
        const agent = agents.find(a => a.id === conversation.agentId) || agents.find(a => a.name === conversation.agentName);
        if (agent) {
          setSelectedAgent(agent);
          setLastRoutedAgentId(conversation.agentId || agent.id);
          setIsAgentSelectedViaCard(false);
        }
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  }, [loadConversationFull, agents, navigate, setActiveConversation, saveSnapshotIfNeeded, cancelActiveStream, setLastRoutedAgentId, clearProductCache]);

  const handleDeleteConversation = useCallback(async (conversationId: string) => {
    try {
      const success = await deleteConversation(conversationId);
      if (success) {
        // If we deleted the current conversation, navigate to home and clear the chat
        if (currentConversationId === conversationId) {
          cancelActiveStream();
          
          // Clear product image cache and product displays on conversation switch
          cancelAllImagePreloads();
          clearProductCache();
          
          // Clear persisted session state
          sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
          sessionStorage.removeItem(SESSION_KEYS.IS_AGENT_VIA_CARD);
          sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);
          
          setMessages([]);
          setSelectedAgent(null);
          setLastRoutedAgentId(null);
          startNewConversation();
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [deleteConversation, currentConversationId, startNewConversation, navigate, cancelActiveStream, setLastRoutedAgentId]);

  const handleClearAllConversations = useCallback(async () => {
    try {
      const success = await clearAllConversations();
      if (success) {
        // Clear the current chat and navigate to home since all conversations are deleted
        cancelActiveStream();
        
        // Clear persisted session state
        sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
        sessionStorage.removeItem(SESSION_KEYS.IS_AGENT_VIA_CARD);
        sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);
        
        setMessages([]);
        setSelectedAgent(null);
        setLastRoutedAgentId(null);
        startNewConversation();
        navigate('/');
      } else {
        console.error('🗑️ handleClearAllConversations: Clear all conversations failed');
      }
    } catch (error) {
      console.error('🗑️ handleClearAllConversations: Failed to clear all conversations:', error);
    }
  }, [clearAllConversations, startNewConversation, navigate, cancelActiveStream, setLastRoutedAgentId]);

  const handleClearChat = useCallback(async () => {
    if (currentConversationId) {
      // If there's a current conversation, delete it from blob storage
      cancelActiveStream();
      await handleDeleteConversation(currentConversationId);
      setLastRoutedAgentId(null);
    } else {
      // If no conversation ID, just clear local state and navigate home
      cancelActiveStream();
      
      // Clear persisted session state
      sessionStorage.removeItem(SESSION_KEYS.SELECTED_AGENT_ID);
      sessionStorage.removeItem(SESSION_KEYS.IS_AGENT_VIA_CARD);
      sessionStorage.removeItem(SESSION_KEYS.LAST_ROUTED_AGENT_ID);
      
      setMessages([]);
      setSelectedAgent(null);
      setLastRoutedAgentId(null);
      startNewConversation();
      navigate('/');
    }
  }, [currentConversationId, handleDeleteConversation, startNewConversation, navigate, cancelActiveStream]);





  // CRITICAL: Check for OIDC callback BEFORE authentication checks
  const currentUrl = window.location.href;
  const hasCode = currentUrl.includes('code=');
  const hasState = currentUrl.includes('state=');
  const isCallback = window.location.pathname.includes('callback') || hasCode || hasState;
  
  if (isCallback) {
    console.log('🚨 CALLBACK DETECTED - Bypassing normal flow!');
    console.log('URL:', currentUrl);
    // Direct render without Router to avoid conflicts
    return <AuthHandler />;
  }

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login returnUrl={window.location.pathname} />;
  }

  return (
    <div className="app h-screen flex" style={{ margin: 0, padding: 0 }}>
      {/* Mobile backdrop */}
      {(isMobile || isTablet) && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" 
          onClick={toggleSidebar}
        />
      )}
      


      {/* Sidebar - responsive using Tailwind */}
      <div className={`
        ${(isMobile || isTablet) 
          ? `fixed left-0 top-0 h-full z-50 transform transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}` 
          : 'relative flex-shrink-0'
        }
        ${(isMobile || isTablet) ? 'w-72' : (isSidebarCollapsed ? 'w-16' : 'w-64')} 
        bg-scout-rail-deep dark:bg-scout-rail-deep-dark transition-all duration-300
      `}
      style={{ marginLeft: 0, paddingLeft: 0 }}>
        <Sidebar
          user={user}
          onLogout={handleLogout}
          onLogoClick={handleLogoClick}
          className="h-full"
          // Conversation history props
          conversations={conversations}
          currentConversationId={currentConversationId}
          onLoadConversation={handleLoadConversation}
          onDeleteConversation={handleDeleteConversation}
          onClearAllConversations={handleClearAllConversations}
          onStartNewConversation={handleNewChat}
          // Sidebar collapse props
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={setIsSidebarCollapsed}
          // User profile for personalization
          userPhotoUrl={graphUserProfile?.photoUrl}
          userDisplayName={graphUserProfile?.displayName}
        />
      </div>

      {/* Main content area - responsive using Tailwind */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative scout-canvas">
        {/* Floating Sidebar Toggle Button - visible on desktop only */}
        {!isMobile && !isTablet && (
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="fixed top-1/2 -translate-y-1/2 z-40 hover:opacity-70 transition-all duration-200 p-2"
            style={{
              left: isSidebarCollapsed ? '16px' : '256px',
              transition: 'left 300ms ease, opacity 200ms ease',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              margin: 0,
              padding: '8px'
            }}
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? (
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        )}
        
        <Routes>
          <Route 
            path="/" 
            element={
              <ChatInterface
                agent={selectedAgent}
                user={user}
                messages={messages}
                onSendMessage={handleSendMessage}
                onBackToAgents={handleBackToAgents}
                onClearChat={handleClearChat}
                isAgentTyping={isAgentTyping}
                isStreaming={isStreaming}
                currentStreamingMessage={currentStreamingMessage}
                toolCalls={toolCalls}
                currentToolCall={currentToolCall}
                onMessagesUpdate={setMessages}
                onRefreshAgents={reloadAgents}
                sessionId={sessionId}
                userId={effectiveUserId}
                onRegenerateMessage={regenerateMessage}
                chatError={chatError}
                onClearError={clearError}
                isStreamingEnabled={isStreamingEnabled}
                onToggleStreaming={toggleStreaming}
                isAgentSelectedViaCard={isAgentSelectedViaCard}
                productDisplays={productDisplays}
                alternativeDisplays={alternativeDisplays}  // For [ALTERNATIVES: anchor_id] replacement
                handleAlternativeSelect={handleAlternativeSelect}
                availableAgents={mentionableAgents}
                // User profile for personalization
                userProfile={graphUserProfile}
                // Landing page props
                LandingPageComponent={LandingPage}
                onLandingPageQuestion={handleLandingPageQuestion}
                recentConversations={conversations}
                onOpenConversation={handleLoadConversation}
                // Layout props for mobile menu
                onToggleSidebar={isMobile || isTablet ? toggleSidebar : undefined}
                isSidebarOpen={sidebarOpen}
              />
            } 
          />
          <Route 
            path="/admin" 
            element={<AdminLandingPage />} 
          />
          <Route 
            path="/admin/panel" 
            element={<AdminPanel />} 
          />
          <Route 
            path="/admin/agent-factory" 
            element={<AgentFactory />} 
          />
          {/* AA-207 self-service routes — temporarily limited to super admins.
              Non-admins hitting the URL directly are redirected home. */}
          <Route
            path="/build-agent"
            element={isSelfServiceAdmin ? <BuildAnAgent /> : <Navigate to="/" replace />}
          />
          <Route
            path="/my-agents"
            element={isSelfServiceAdmin ? <MyBuiltAgents /> : <Navigate to="/" replace />}
          />
          <Route 
            path="/dev-admin" 
            element={<DevAdmin />} 
          />
          <Route 
            path="/admin/aldo" 
            element={<AldoDashboard />} 
          />
          <Route 
            path="/admin/hrpolicy" 
            element={<HRPolicyDashboard />} 
          />
          <Route 
            path="/admin/pwms" 
            element={<PWMSDashboard />}
          />
          <Route 
            path="/admin/product" 
            element={<ProductDashboard />}
          />
          <Route 
            path="/admin/unifirst_docs" 
            element={<UniFirstDocsDashboard />}
          />
          <Route 
            path="/admin/orientation" 
            element={<OrientationDashboard />}
          />
          <Route 
            path="/admin/iris" 
            element={<IRISDashboard />}
          />
          <Route 
            path="/admin/service-desk" 
            element={<ServiceDeskDashboard />}
          />
          <Route 
            path="/admin/owensboro2" 
            element={<OwensboroDashboard />}
          />
          <Route 
            path="/admin/direct_embroidery" 
            element={<DirectEmbroideryDashboard />}
          />
          <Route 
            path="/admin/management" 
            element={<IndexingDashboard />}
          />
          {/* Legacy route redirect */}
          <Route 
            path="/admin/indexing" 
            element={<IndexingDashboard />}
          />
          {/* Patch Notes page temporarily disabled
          <Route 
            path="/patch-notes" 
            element={<PatchNotes />} 
          />
          */}
          
          {/* Graph API Test Page */}
          <Route 
            path="/graph-test" 
            element={<GraphAPITest />} 
          />
          
          {/* Authentication Error Route */}
          <Route 
            path="/auth-error" 
            element={<AuthError />} 
          />
          {/* OIDC Authentication Routes - Flexible catch-all pattern */}
          <Route 
            path="/auth/*" 
            element={<AuthHandler />} 
          />
          <Route 
            path="/auth/test" 
            element={<div style={{padding: '50px', backgroundColor: 'lime', fontSize: '24px'}}>🧪 TEST ROUTE WORKS! React Router is functioning.</div>} 
          />

        </Routes>
      </div>

      {/* Global Debug Panel - Toggle with Ctrl+Shift+D */}
      <AuthDebug isVisible={showDebug} />
      
      {/* Dev Testing Panel - Shows on localhost (always) or UAT (super-admin only) */}
      <DevTestingPanel userRoles={getUserRoles()} />

    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
